import { GridCellParams, GridRowParams } from '@mui/x-data-grid';
import { DataGridPro, GridSortModel } from '@mui/x-data-grid-pro';

import { useTranslation } from 'react-i18next';
import { IconButton, Paper, Tooltip, Typography, styled } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PasswordIcon from '@mui/icons-material/Password';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import { UserAccount } from '../../../data/models/user-account.model';
import { PagedList } from '../../../data/models/paged-list.model';
import { GridPaginationModel, GridCallbackDetails } from '@mui/x-data-grid-pro';
import StatusIndicator from './StatusIndicator';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'UserDataTable';

export const classes = {
  container: `${PREFIX}-container`,
  title: `${PREFIX}-title`,
  grid: `${PREFIX}-grid`,
};

const DataTableContainer = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },

  [`& .${classes.title}`]: {
    display: 'flex',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: 16,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  [`& .${classes.grid}`]: {
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
    '& .MuiDataGrid-row:hover': {
      cursor: 'pointer',
    },
  },
}));

interface UserDataTableProps {
  users: PagedList<UserAccount>;
  loading: boolean;
  readOnly: boolean;
  pageSize: number;
  onboarding: boolean;
  onEdit: (id: number) => void;
  onDelete: (id: number, userCategoryTypeID: number) => void;
  onPageChange: (val: number) => void;
  onPageSizeChange: (val: number) => void;
  onPasswordReset?: (id: number, email: string) => void;
  onSendActivationEmail?: (id: number, email: string) => void;
  onSortModelChange: (model: GridSortModel) => void;
  sortModel: GridSortModel;
}

export default function UserDataTable({
  users,
  loading,
  readOnly,
  pageSize,
  onboarding,
  onEdit,
  onDelete,
  onPageChange,
  onPageSizeChange,
  onPasswordReset,
  onSendActivationEmail,
  onSortModelChange,
  sortModel,
}: UserDataTableProps) {
  const { t } = useTranslation('common');
  const isSuperReaderUser = useIsSuperReaderUser();

  const columns = [
    {
      field: 'userLastName',
      headerName: t('users.lastName'),
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <p onClick={() => onEdit(params.row.userAccountID)}>
          {params.row.userLastName}
        </p>
      ),
    },
    {
      field: 'userFirstName',
      headerName: t('users.firstName'),
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <p onClick={() => onEdit(params.row.userAccountID)}>
          {params.row.userFirstName}
        </p>
      ),
    },
    {
      field: 'emailAddress',
      headerName: t('users.email'),
      flex: 2,
    },
    {
      field: 'IsActive',
      headerName: t('common.active'),
      width: 80,
      renderCell: (params: GridCellParams) => (
        <StatusIndicator user={params.row} />
      ),
    },
    {
      field: 'Actions',
      headerName: ' ',
      width: 155,
      hide: readOnly,
      renderCell: (params: GridCellParams) => {
        return (
          <>
            {!isSuperReaderUser && (
              <Tooltip title={t('users.editUser')}>
                <IconButton
                  data-testid="edit-user"
                  onClick={() => onEdit(params.row.userAccountID)}
                  size="large">
                  <EditIcon color="primary" />
                </IconButton>
              </Tooltip>
            )}
            {!isSuperReaderUser && (
              <Tooltip title={t('users.deleteUser')}>
                <IconButton
                  data-testid="delete-user"
                  onClick={(evt) =>
                    onDelete(
                      params.row.userAccountID,
                      params.row.userCategoryTypeID,
                    )
                  }
                  color="warning"
                  size="large">
                  <DeleteForeverIcon color="error" />
                </IconButton>
              </Tooltip>
            )}
            {onPasswordReset && !isSuperReaderUser && (
              <Tooltip title={t('users.passwordReset')}>
                <IconButton
                  data-testid="reset-pwd"
                  onClick={() => {
                    onPasswordReset(
                      params.row.userAccountID,
                      params.row.emailAddress,
                    );
                  }}
                  size="large"
                  color="default">
                  <PasswordIcon />
                </IconButton>
              </Tooltip>
            )}
            {onSendActivationEmail && !isSuperReaderUser && (
              <Tooltip title={t('users.sendActivation')}>
                <IconButton
                  data-testid="reset"
                  onClick={() => {
                    onSendActivationEmail(
                      params.row.userAccountID,
                      params.row.emailAddress,
                    );
                  }}
                  size="large"
                  color="default">
                  <ForwardToInboxIcon />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
      sortable: false,
    },
  ];

  const paginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== users.currentPage - 1) {
      onPageChange(model.page + 1);
    }
    if (model.pageSize !== pageSize) {
      onPageSizeChange(model.pageSize);
    }
  };

  return (
    <DataTableContainer data-testid="user-table" className={classes.container}>
      <Typography className={classes.title}>
        {onboarding
          ? t('users.onboardingUsersTitle')
          : t('users.onboardedUsersTitle')}
      </Typography>
      <DataGridPro
        getRowId={(row) => row.userAccountID!}
        rows={users.items}
        columns={columns}
        rowCount={users.itemCount}
        autoHeight={true}
        loading={loading}
        className={classes.grid}
        checkboxSelection={false}
        disableColumnMenu={true}
        disableColumnSelector={true}
        pagination={true}
        paginationMode="server"
        sortingMode="server"
        initialState={{
          sorting: {
            sortModel: [{ field: 'userLastName', sort: 'asc' }],
          },
        }}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        sortingOrder={['asc', 'desc']}
        paginationModel={{ page: users.currentPage - 1, pageSize }}
        pageSizeOptions={[10, 20, 30, 40]}
        onPaginationModelChange={paginationChange}
      />
    </DataTableContainer>
  );
}
